import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';

import { WorkspacePermissions } from '../access/workspace-permission.directive';
import { OrganizationContextService } from '../guards/organization-context.service';
import { AccountingService } from '../services/accounting/accounting.service';
import { LeaseService } from '../services/leases/lease.service';
import { MaintenanceApiService } from '../services/maintenance/maintenance.service';
import { StripeConnectService, PaymentReadiness } from '../services/payments/stripe-connect.service';
import { SubscriptionService, SubscriptionStatusResponse, planIncludes } from '../services/subscriptions/subscription.service';
import {
  SETUP_GUIDE_HIDDEN_STORAGE_PREFIX,
  SETUP_GUIDE_STEPS,
  SetupFacts,
  SetupGuideStep,
  SetupStepId,
  accountingDefaultsDone,
  hasAny,
  lateFeePolicyDone,
  onlinePaymentsDone,
  teamInvitedDone,
  workspaceBasicsDone,
} from './setup-guide.config';

/** What the caller already loaded. `undefined` means "not loaded, fetch it"; `null` means "lookup failed". */
export type KnownSetupData = {
  propertyCount?: number | null;
  paymentStatus?: PaymentReadiness | null;
};

type Page = { content?: unknown[] | null; totalElements?: number | null };

/**
 * Workspace onboarding checklist: who sees it, which steps apply, what is done, and the per-workspace "Hide guide" choice.
 * Only people who can change workspace settings (owner/admin) see it; it never decides access to anything else.
 */
@Injectable({ providedIn: 'root' })
export class SetupGuideService {
  private readonly permissions = inject(WorkspacePermissions);
  private readonly organizations = inject(OrganizationContextService);
  private readonly accounting = inject(AccountingService);
  private readonly leases = inject(LeaseService);
  private readonly maintenance = inject(MaintenanceApiService);
  private readonly stripeConnect = inject(StripeConnectService);
  private readonly subscriptions = inject(SubscriptionService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Choices made this session; anything else is read from storage (no signal writes, so it is safe inside computed()). */
  private readonly hiddenChoices = signal<Readonly<Record<string, boolean>>>({});
  /** Workspace whose guide was reopened from the shell this session: shown even when every required step is done. */
  readonly reopenedOrgId = signal<string | null>(null);

  /** Owner/admin only: workspace settings rights across the whole workspace, never staff limited to assigned properties. */
  readonly canUse = computed(() => {
    const workspace = this.permissions.workspace();
    if (!workspace || workspace.accessScopeType === 'ASSIGNED_PROPERTIES_ONLY') return false;
    return this.permissions.can('ORG_EDIT_SETTINGS');
  });

  /** Steps this person can act on: a step whose page they cannot open is left out rather than shown as undoable. */
  availableSteps(orgId = this.permissions.workspace()?.orgId): SetupGuideStep[] {
    if (!this.canUse()) return [];
    const workspace = this.permissions.workspace()!;
    if (workspace.orgId !== orgId) return [];
    const plan = this.subscriptions.statusFor(workspace.orgId);
    if (!plan?.active) return [];
    const isPropertyManager = workspace.orgType === 'PROPERTY_MANAGER';
    return SETUP_GUIDE_STEPS.filter(step =>
      (step.id !== 'owners' || isPropertyManager) &&
      (!step.planFeature || planIncludes(plan, step.planFeature)) &&
      (!step.planCapacity || hasCapacity(plan, step.planCapacity)) &&
      this.permissions.canVisit(step.route) && (!step.permissions || this.permissions.can(step.permissions)));
  }

  ensurePlan(orgId = this.permissions.workspace()?.orgId): Promise<void> {
    return orgId ? this.subscriptions.ensureWorkspaceStatus(orgId) : Promise.resolve();
  }

  planPending(): boolean {
    const workspace = this.permissions.workspace();
    return !!workspace && this.subscriptions.statusFor(workspace.orgId) === undefined;
  }

  isHidden(orgId: string | null | undefined): boolean {
    if (!orgId) return false;
    return this.hiddenChoices()[orgId] ?? this.readHidden(orgId);
  }

  hide(orgId: string): void {
    this.hiddenChoices.update(current => ({ ...current, [orgId]: true }));
    if (this.reopenedOrgId() === orgId) this.reopenedOrgId.set(null);
    this.writeHidden(orgId, true);
  }

  reopen(orgId: string): void {
    this.hiddenChoices.update(current => ({ ...current, [orgId]: false }));
    this.reopenedOrgId.set(orgId);
    this.writeHidden(orgId, false);
  }

  /** Loads only what the available steps need; every failed lookup leaves its step unknown (shown as to do). */
  async loadFacts(orgId: string, steps: readonly SetupGuideStep[], known: KnownSetupData = {}): Promise<SetupFacts> {
    if (this.permissions.workspace()?.orgId !== orgId) return {};
    // Revalidate stale callers against current plan, organization type and action permissions before making requests.
    const eligible = new Set(this.availableSteps().map(step => step.id));
    const wanted = new Set<SetupStepId>(steps.filter(step => eligible.has(step.id)).map(step => step.id));
    const safe = <T>(source: () => Observable<T>): Promise<T | null> =>
      firstValueFrom(source()).catch(() => null);

    const needsOrganization = wanted.has('workspace-basics') || wanted.has('late-fees');
    const [organization, paymentStatus, accounts, bankAccounts, propertyCount, leases, owners, activeStaff, invitedStaff, companies] = await Promise.all([
      needsOrganization ? safe(() => this.organizations.getOrganization(orgId)) : null,
      !wanted.has('online-payments') ? null
        : known.paymentStatus !== undefined ? known.paymentStatus : safe(() => this.stripeConnect.getStatus(orgId)),
      wanted.has('accounting') ? safe(() => this.accounting.listChartOfAccounts(orgId)) : null,
      wanted.has('accounting') ? safe(() => this.accounting.listBankAccounts(orgId)) : null,
      !wanted.has('first-property') ? null
        : known.propertyCount !== undefined ? known.propertyCount : safe(() => this.organizations.getTeamProperties(orgId)).then(page => pageCount(page as Page | null)),
      wanted.has('first-lease') ? safe(() => this.leases.listLeases(orgId, 0, 1)) : null,
      wanted.has('owners') ? safe(() => this.organizations.getOwners(orgId, { size: 1 })) : null,
      wanted.has('team') ? safe(() => this.organizations.getStaffMembers(orgId, { status: 'ACTIVE', size: 2 })) : null,
      wanted.has('team') ? safe(() => this.organizations.getStaffMembers(orgId, { status: 'INVITED', size: 1 })) : null,
      wanted.has('maintenance-companies') ? safe(() => this.maintenance.listCompanies(orgId)) : null,
    ]);

    const facts: SetupFacts = {
      'workspace-basics': workspaceBasicsDone(organization),
      'online-payments': onlinePaymentsDone(paymentStatus),
      accounting: accountingDefaultsDone(accounts, bankAccounts),
      'late-fees': lateFeePolicyDone(organization),
      'first-property': hasAny(propertyCount),
      'first-lease': hasAny(pageCount(leases)),
      owners: hasAny(pageCount(owners)),
      team: teamInvitedDone(pageCount(activeStaff), pageCount(invitedStaff)),
      'maintenance-companies': Array.isArray(companies) ? companies.length > 0 : null,
    };
    return Object.fromEntries(Object.entries(facts).filter(([id]) => wanted.has(id as SetupStepId))) as SetupFacts;
  }

  private readHidden(orgId: string): boolean {
    if (!this.isBrowser) return false;
    try {
      return window.localStorage.getItem(SETUP_GUIDE_HIDDEN_STORAGE_PREFIX + orgId) === '1';
    } catch {
      // Storage can be blocked (private mode, site data off): the guide simply shows again.
      return false;
    }
  }

  private writeHidden(orgId: string, hidden: boolean): void {
    if (!this.isBrowser) return;
    try {
      if (hidden) window.localStorage.setItem(SETUP_GUIDE_HIDDEN_STORAGE_PREFIX + orgId, '1');
      else window.localStorage.removeItem(SETUP_GUIDE_HIDDEN_STORAGE_PREFIX + orgId);
    } catch {
      // The choice still holds for this session through the signal.
    }
  }
}

function pageCount(page: Page | null | undefined): number | null {
  if (!page) return null;
  if (typeof page.totalElements === 'number') return page.totalElements;
  return Array.isArray(page.content) ? page.content.length : null;
}

function hasCapacity(plan: SubscriptionStatusResponse, kind: 'property' | 'staff'): boolean {
  const allowed = kind === 'property' ? plan.canAddProperty : plan.canAddStaff;
  if (typeof allowed === 'boolean') return allowed;
  if (kind === 'property' ? plan.unlimitedProperties : plan.unlimitedStaff) return true;
  const limit = Number(kind === 'property' ? plan.maxPropertiesPerOrg : plan.maxStaffPerOrg);
  const used = Number((kind === 'property' ? plan.propertiesUsed : plan.staffUsed) ?? 0);
  return Number.isFinite(limit) && limit > 0 && used < limit;
}
